import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { DatabaseZap, Lock, AlertTriangle } from "lucide-react";
import { streamAsk } from "@/lib/mongoMcp";
import ChatList from "@/components/mongo/ChatList";
import ChatMessage from "@/components/mongo/ChatMessage";
import ChatComposer from "@/components/mongo/ChatComposer";

const EXAMPLES = ["How many visits were completed in the last 7 days?", "Which sites have the most scheduled visits?", "How many signed forms are in eSource?"];
const toolOf = (fn) => (fn || "").replace(/^mongo_/, "").replace(/_/g, "-");

function applyEvent(acc, ev) {
    if (ev.type === "step_start") return { ...acc, steps: [...acc.steps, { id: ev.id, tool: toolOf(ev.tool), status: "running" }] };
    if (ev.type === "step") return { ...acc, steps: acc.steps.map((s) => (s.id === ev.id ? { ...ev.step, id: ev.id } : s)) };
    if (ev.type === "delta") return { ...acc, content: acc.content + ev.text };
    if (ev.type === "notice") return { ...acc, notices: [...acc.notices, ev.text] };
    if (ev.type === "error") return { ...acc, error: ev.message };
    return acc;
}

export default function AskDatabasePage() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const [status, setStatus] = useState(null);
    const [chats, setChats] = useState([]);
    const [chat, setChat] = useState(null);
    const [live, setLive] = useState(null);
    const bottom = useRef(null);

    const loadChats = useCallback(async () => {
        try { setChats((await api.get("/admin/mongo-mcp/chats")).data.items || []); }
        catch (e) { toast.error(formatApiErrorDetail(e)); }
    }, []);
    useEffect(() => {
        loadChats();
        api.get("/admin/mongo-mcp/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
    }, [loadChats]);
    useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [live, chat]);

    const open = async (id) => {
        if (live) return;
        try { setChat((await api.get(`/admin/mongo-mcp/chats/${id}`)).data); }
        catch (e) { toast.error(formatApiErrorDetail(e)); }
    };
    const remove = async (id) => {
        try { await api.delete(`/admin/mongo-mcp/chats/${id}`); if (chat?.id === id) setChat(null); loadChats(); }
        catch (e) { toast.error(formatApiErrorDetail(e)); }
    };
    const ask = async (q) => {
        let c = chat;
        try { if (!c) c = (await api.post("/admin/mongo-mcp/chats", {})).data; }
        catch (e) { return toast.error(formatApiErrorDetail(e)); }
        const base = { ...c, title: c.messages?.length ? c.title : q.slice(0, 80), messages: [...(c.messages || []), { role: "user", content: q }] };
        setChat(base);
        let acc = { role: "assistant", content: "", steps: [], notices: [] };
        setLive(acc);
        try {
            await streamAsk(c.id, q, (ev) => {
                if (ev.type === "done") { setChat({ ...base, messages: [...base.messages, ev.message] }); return; }
                acc = applyEvent(acc, ev);
                setLive(acc);
            });
        } catch (e) {
            toast.error(e.message);
            setChat({ ...base, messages: [...base.messages, { ...acc, error: e.message }] });
        } finally { setLive(null); loadChats(); }
    };

    const lock = chat?.lock || (isSuper ? "all companies" : `company ${(user?.client_ids || []).join(", ") || "none"}`);
    const off = status && (!status.configured || !status.reachable);
    return (
        <div className="flex h-[calc(100vh-3.5rem)]" data-testid="ask-database-page">
            <ChatList chats={chats} activeId={chat?.id} onSelect={open} onNew={() => !live && setChat(null)} onDelete={remove} />
            <section className="flex-1 min-w-0 flex flex-col">
                <header className="px-8 py-5 border-b border-white/10 flex items-center gap-4">
                    <DatabaseZap className="w-5 h-5 text-[#7C3AED]" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                        <h1 className="font-display text-2xl tracking-tight truncate" data-testid="ask-title">{chat?.title || "Ask the database"}</h1>
                        <div className="text-[11px] text-white/45">Live, read-only answers from aiservice, eSource and regez</div>
                    </div>
                    <span className="flex items-center gap-1.5 text-[11px] font-mono px-2.5 py-1 rounded-full border border-[#7C3AED]/40 text-[#C4B5FD]" data-testid="ask-lock-badge">
                        <Lock className="w-3 h-3" /> {lock}
                    </span>
                </header>
                {off && (
                    <div className="mx-8 mt-4 text-xs text-amber-300 border border-amber-400/30 bg-amber-400/5 rounded-md px-4 py-2.5 flex items-center gap-2" data-testid="ask-unavailable">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {!status.configured ? "MongoDB MCP is not configured in this environment — live data cannot be read here." : `The MongoDB MCP server is not reachable: ${status.error || "no answer"}.`}
                    </div>
                )}
                <div className="flex-1 overflow-auto px-8 py-6 space-y-5" data-testid="ask-thread">
                    {!chat?.messages?.length && !live && <Examples onPick={ask} disabled={off || !status} />}
                    {(chat?.messages || []).map((m, i) => <ChatMessage key={i} msg={m} index={i} />)}
                    {live && <ChatMessage msg={live} index="live" live />}
                    <div ref={bottom} />
                </div>
                <ChatComposer onSend={ask} disabled={!!live || off || !status} placeholder="Ask about visits, sites, forms, jobs…" />
            </section>
        </div>
    );
}

function Examples({ onPick, disabled }) {
    return (
        <div className="max-w-2xl pt-10" data-testid="ask-examples">
            <div className="font-display text-3xl tracking-tight text-white/90">What do you want to know?</div>
            <p className="text-sm text-white/45 mt-2">Each answer shows the lookups it ran — open one to see the exact query and its company lock.</p>
            <div className="flex flex-wrap gap-2 mt-6">
                {EXAMPLES.map((q, i) => (
                    <button key={q} onClick={() => onPick(q)} disabled={disabled} data-testid={`ask-example-${i}`}
                        className="text-xs px-3 py-2 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-[#7C3AED]/60 hover:bg-[#7C3AED]/10 disabled:opacity-40 transition-colors">
                        {q}
                    </button>
                ))}
            </div>
        </div>
    );
}
